import { Avatar, Divider, Tag } from "antd";
import {
  MapPin,
  Clock,
  Mail,
  Phone,
  Award,
  ListChecks,
  HelpCircle,
  Shield,
  Image as ImageIcon,
} from "lucide-react";
import { format } from "date-fns";
import { getPrimaryMediaUrl } from "commons/helper";
import {
  HSN_CODE_OPTIONS,
  SHOW_IN_LISTINGS_COPY,
} from "pages/Events/commons/events.helpers";

function OverviewTab({ event }) {
  const loc = event.location ?? {};
  const tickets = event.tickets ?? [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* ── Left (main content) ── */}
      <div className="lg:col-span-2 space-y-8">
        {event.description && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold">About this Event</h3>
            <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed text-sm">
              {event.description}
            </p>
          </section>
        )}

        {loc.name && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold">Venue</h3>
            <div className="flex items-start space-x-3 p-4 rounded-xl bg-muted/30 border">
              <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold text-sm">{loc.name}</p>
                <p className="text-xs text-muted-foreground">
                  {[
                    loc.address_line_1,
                    loc.address_line_2,
                    loc.city,
                    loc.state,
                    loc.postal_code,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
                {loc.contact_details?.email && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {loc.contact_details.email}
                  </p>
                )}
                {loc.contact_details?.phone_number && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {loc.contact_details.phone_number}
                  </p>
                )}
                {loc.operating_hours?.opening_time && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {loc.operating_hours.opening_time} –{" "}
                    {loc.operating_hours.closing_time}
                    {loc.operating_hours.days_of_week?.length > 0 &&
                      ` (${loc.operating_hours.days_of_week.join(", ")})`}
                  </p>
                )}
              </div>
            </div>
            {loc.amenities?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Venue Amenities
                </p>
                <div className="flex flex-wrap gap-2">
                  {loc.amenities.map((a, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-1.5 text-xs bg-muted px-2.5 py-1 rounded-full border"
                    >
                      {a.icon_url && (
                        <img
                          src={a.icon_url}
                          alt={a.name}
                          className="h-3.5 w-3.5 rounded-full object-cover"
                        />
                      )}
                      {a.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {event.organiser_details?.name && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold">Organiser</h3>
            <div className="flex items-center gap-4">
              {event.organiser_details.logo_url && (
                <Avatar
                  src={event.organiser_details.logo_url}
                  size={56}
                  shape="circle"
                />
              )}
              <div>
                <p className="font-bold">{event.organiser_details.name}</p>
                {event.organiser_details.total_events_hosted != null && (
                  <p className="text-xs text-muted-foreground">
                    {event.organiser_details.total_events_hosted} events hosted
                  </p>
                )}
                {event.organiser_details.hosting_since && (
                  <p className="text-xs text-muted-foreground">
                    Hosting since {event.organiser_details.hosting_since}
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {event.amenities?.length > 0 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Award className="h-5 w-5 text-primary" /> Amenities
            </h3>
            <div className="flex flex-wrap gap-2">
              {event.amenities.map((a, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1.5 text-sm bg-muted px-3 py-1.5 rounded-full border"
                >
                  {a.icon_url && (
                    <img
                      src={a.icon_url}
                      alt={a.name}
                      className="h-4 w-4 rounded-full object-cover"
                    />
                  )}
                  {a.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {event.activities?.length > 0 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-primary" /> Activities
            </h3>
            <div className="space-y-3">
              {event.activities.map((a, i) => {
                const img = getPrimaryMediaUrl(a.media);
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-xl border bg-muted/20"
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={a.name}
                        className="h-14 w-14 rounded-lg object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">{a.name}</p>
                      {a.description && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {a.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {event.schedule_info?.length > 0 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" /> Schedule
            </h3>
            <div className="space-y-0">
              {event.schedule_info.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-3 border-b last:border-0"
                >
                  <div>
                    <p className="font-medium text-sm">{s.name}</p>
                    {s.date && (
                      <p className="text-xs text-muted-foreground">{s.date}</p>
                    )}
                  </div>
                  <span className="font-mono text-primary font-semibold text-sm">
                    {s.value}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {tickets.length > 0 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-6">
            <h3 className="text-xl font-bold">Ticket Tiers</h3>
            <div className="space-y-4">
              {tickets.map((ticket, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 rounded-xl border bg-muted/20"
                >
                  <div className="space-y-1">
                    <p className="font-bold">{ticket.name}</p>
                    {ticket.description && (
                      <p className="text-xs text-muted-foreground">
                        {ticket.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {ticket.quantity} total tickets available
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">
                      {ticket.price === 0
                        ? "Free"
                        : `₹${ticket.price.toLocaleString()}`}
                    </p>
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                      Active
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {event.partners?.length > 0 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold">Partners</h3>
            <div className="flex flex-wrap gap-4">
              {event.partners.map((p, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 border rounded-xl px-4 py-2.5"
                >
                  {p.logo_url && (
                    <Avatar src={p.logo_url} size={28} shape="square" />
                  )}
                  <span className="font-medium text-sm">{p.name}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {event.faq?.length > 0 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" /> FAQ
            </h3>
            <div className="space-y-4">
              {event.faq.map((f, i) => (
                <div key={i}>
                  <p className="font-semibold text-sm">{f.question}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {f.answer}
                  </p>
                  {i < event.faq.length - 1 && <Divider className="my-3" />}
                </div>
              ))}
            </div>
          </section>
        )}

        {event.things_to_know?.length > 0 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" /> Things to Know
            </h3>
            <ul className="space-y-2">
              {event.things_to_know.map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                >
                  <span className="text-primary mt-0.5 shrink-0">•</span>
                  {t}
                </li>
              ))}
            </ul>
          </section>
        )}

        {event.terms_and_conditions?.title && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold">
              {event.terms_and_conditions.title}
            </h3>
            {event.terms_and_conditions.sub_title && (
              <p className="text-sm text-muted-foreground">
                {event.terms_and_conditions.sub_title}
              </p>
            )}
            <div className="space-y-4">
              {event.terms_and_conditions.sections?.map((s, i) => (
                <div key={i}>
                  <p className="font-semibold text-sm">{s.heading}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {s.content}
                  </p>
                  {i < event.terms_and_conditions.sections.length - 1 && (
                    <Divider className="my-3" />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {event.media?.length > 1 && (
          <section className="rounded-2xl border bg-card p-8 shadow-sm space-y-4">
            <h3 className="text-xl font-bold">Gallery</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {event.media.map((m, i) => (
                <img
                  key={i}
                  src={m.value}
                  alt={`Media ${i + 1}`}
                  className="w-full aspect-video object-cover rounded-xl border"
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ── Right (sidebar) ── */}
      <div className="space-y-6">
        <div className="rounded-2xl border bg-card p-6 shadow-sm space-y-4">
          <h4 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
            Event Details
          </h4>
          <div className="space-y-3 text-sm">
            {event.start_date_time && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Start</span>
                <span className="font-medium text-right">
                  {format(
                    new Date(event.start_date_time),
                    "MMM d, yyyy • h:mm a"
                  )}
                </span>
              </div>
            )}
            {event.end_date_time && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">End</span>
                <span className="font-medium text-right">
                  {format(
                    new Date(event.end_date_time),
                    "MMM d, yyyy • h:mm a"
                  )}
                </span>
              </div>
            )}
            {event.event_type && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="font-medium">
                  {event.event_type.replace("_", " ")}
                </span>
              </div>
            )}
            {event.event_access && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Access</span>
                <span className="font-medium">{event.event_access}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Open to everyone</span>
              <span className="font-medium">
                {event.is_public ? "Yes" : "No"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Listings</span>
              <span className="font-medium">
                {event.show_in_listings === false
                  ? SHOW_IN_LISTINGS_COPY.valueOff
                  : SHOW_IN_LISTINGS_COPY.valueOn}
              </span>
            </div>
            {/* <div className="flex justify-between">
              <span className="text-muted-foreground">HSN Code</span>
              <span className="font-medium">
                {(() => {
                  const hsn = HSN_CODE_OPTIONS.find((h) => h.value === event.hsn_code);
                  return hsn ? `${hsn.label} (${hsn.value})` : event.hsn_code;
                })()}
              </span>
            </div> */}
            {event.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {event.tags.map((t) => (
                  <Tag key={t} className="text-xs">
                    {t}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        </div>

        {(event.contact_details?.email ||
          event.contact_details?.phone_number) && (
          <div className="rounded-2xl border bg-card p-6 shadow-sm space-y-4">
            <h4 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
              Contact
            </h4>
            <div className="space-y-2 text-sm">
              {event.contact_details.email && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4 shrink-0" />
                  <a
                    href={`mailto:${event.contact_details.email}`}
                    className="hover:underline truncate"
                  >
                    {event.contact_details.email}
                  </a>
                </p>
              )}
              {event.contact_details.phone_number && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4 shrink-0" />
                  {event.contact_details.phone_number}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OverviewTab;
